import { supabase } from "./supabase";
import type { PageContextInfo } from "@/hooks/usePageContext";

export interface DiscoveryConversationMessage {
  role: "user" | "assistant";
  content: string;
}

export interface DiscoveryServiceRecommendation {
  name: string;
  slug: string;
  category: string;
  description: string;
  confidence: number;
}

export interface DiscoveryServiceMatch extends DiscoveryServiceRecommendation {
  processing_time?: string | null;
  fee?: string | null;
}

export interface DiscoveryFAQMatch {
  question: string;
  answer: string;
  category: string;
  source: "general" | "service";
  service_name?: string | null;
  service_slug?: string | null;
  href: string;
  confidence: number;
}

export interface DiscoveryResourceMatch {
  name: string;
  resource_type?: string | null;
  url: string;
  service_name: string;
  service_slug: string;
  category: string;
  description: string;
  file_size_label?: string | null;
  confidence: number;
}

export interface PublicDiscoveryResponse {
  content: string;
  recommendations: DiscoveryServiceRecommendation[];
  searchResults: {
    services: DiscoveryServiceMatch[];
    faqs: DiscoveryFAQMatch[];
    resources: DiscoveryResourceMatch[];
  };
  source: "ai" | "fallback";
}

interface DiscoverPublicContentParams {
  message: string;
  conversationHistory?: DiscoveryConversationMessage[];
  pageContext?: PageContextInfo;
  experience?: "chat" | "search";
  language?: string;
}

export async function discoverPublicContent(
  params: DiscoverPublicContentParams,
): Promise<PublicDiscoveryResponse> {
  const { data, error } = await supabase.functions.invoke(
    "ai-service-discovery",
    {
      body: {
        message: params.message,
        conversation_history: params.conversationHistory || [],
        page_context: params.pageContext,
        experience: params.experience || "chat",
        language: params.language,
      },
    },
  );

  if (error) {
    throw error;
  }

  if (!data?.success) {
    throw new Error(data?.error || "Failed to search public content");
  }

  const responseData = data.data || {};
  const searchResults = responseData.search_results || {};

  return {
    content:
      responseData.content || "Sorry, I could not process your request.",
    recommendations: Array.isArray(responseData.recommendations)
      ? responseData.recommendations
      : [],
    searchResults: {
      services: Array.isArray(searchResults.services)
        ? searchResults.services
        : [],
      faqs: Array.isArray(searchResults.faqs) ? searchResults.faqs : [],
      resources: Array.isArray(searchResults.resources)
        ? searchResults.resources
        : [],
    },
    source: responseData.source === "fallback" ? "fallback" : "ai",
  };
}
